import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, User } from "lucide-react";
import axios from "axios";

import { API } from "@/apiConfig";

const DEFAULT_PAGE = {
  article_cta_title: "Envie d'aller plus loin ?",
  article_cta_text: "Prenez rendez-vous pour un accompagnement personnalisé.",
  article_cta_label: "Prendre rendez-vous",
};

function formatDate(dateStr) {
  if (!dateStr) return "";
  try { return new Date(dateStr).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
}

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/pages/blog`)
      .then(r => { if (r.data?.content) setPage(prev => ({ ...prev, ...r.data.content })); })
      .catch(() => {});
    axios.get(`${API}/public/articles/${slug}`)
      .then(r => { setArticle(r.data); document.title = `${r.data.title} — Eudemonie`; })
      .catch(() => navigate("/blog"))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) return <div className="min-h-screen pt-32 flex items-center justify-center"><div className="w-8 h-8 border-2 border-sage border-t-transparent rounded-full animate-spin" /></div>;
  if (!article) return null;

  return (
    <div className="pt-20">
      {/* Header */}
      {article.image && (
        <div className="relative h-64 md:h-96 overflow-hidden">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" />
        </div>
      )}

      <section className="section-gap bg-bone-light">
        <div className="section-container">
          <div className="max-w-3xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Link to="/blog" className="inline-flex items-center gap-2 font-manrope text-sm text-charcoal-muted hover:text-sage transition-colors mb-8">
                <ArrowLeft size={16} /> Retour au blog
              </Link>

              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-2 text-xs text-charcoal-muted font-manrope">
                  <Calendar size={13} className="text-sage" />
                  {formatDate(article.published_at)}
                </div>
                <div className="flex items-center gap-2 text-xs text-charcoal-muted font-manrope">
                  <User size={13} className="text-sage" />
                  {article.author}
                </div>
              </div>

              <h1 className="font-playfair text-4xl md:text-5xl text-charcoal mb-8">{article.title}</h1>

              {article.excerpt && (
                <p className="font-manrope text-lg text-charcoal-muted leading-relaxed mb-8 italic border-l-2 border-sage pl-4">{article.excerpt}</p>
              )}

              <div className="prose-sophro" data-testid="article-content" dangerouslySetInnerHTML={{ __html: article.content }} />
            </motion.div>

            {/* CTA */}
            <div className="mt-16 p-8 bg-sage rounded-2xl text-white text-center">
              <h3 className="font-playfair text-2xl mb-3">{page.article_cta_title}</h3>
              <p className="font-manrope text-sm text-white/80 mb-5">{page.article_cta_text}</p>
              <Link to="/prendre-rendez-vous" className="rounded-full bg-white text-sage-dark hover:bg-bone px-6 py-3 font-manrope font-medium text-sm transition-all duration-300 shadow-sm inline-block">
                {page.article_cta_label}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
